import { TEDCApplicant } from "@smvtech/x-flux";
import dayjs, { Dayjs } from "dayjs";

type ProcessedDropdownOptions = {
	processedOptions: string[];
	optionLabelMap: Record<string, string>;
};

const CHILD_INDEX_OFFSET = 100;

export const getFullName = (
	firstName: string,
	middleName: string | undefined | null,
	lastName: string | null | undefined
) => `${firstName || ""} ${middleName || ""} ${lastName || ""}`.replace(/\s\s+/g, " ");

export function addEllipsis(str: string, length = 30) {
	if (str.length > length) {
		const firstPart = str.slice(0, (length / 2) | 0);
		const secondPart = str.slice((-length / 2) | 0);
		return `${firstPart}...${secondPart}`;
	}
	return str;
}

export function getDummyTravellerSortKey(name: string): number {
	if (!name) return 0;
	const normalizedName = name.trim();
	const match = normalizedName.match(/^(Traveller|Child)\s*#\s*(\d+)$/i);
	if (!match) return 0;
	const isTraveller = match[1].toLowerCase() === "traveller";
	const number = parseInt(match[2] || "0", 10);
	return isTraveller ? number : CHILD_INDEX_OFFSET + number;
}

export function isDummyTravellerName(name: string | null | undefined): boolean {
	if (!name) return false;
	const normalizedName = name.trim();
	const pattern = /^(Traveller|Child)\s*#\s*\d+$/i;
	return pattern.test(normalizedName);
}

export function extractDefaultAnswer(value: string | null | undefined): string | null {
	if (value == null || value === "") {
		return null;
	}
	const trimmed = value.trim();
	const match = trimmed.match(/^\{\{(.+)\}\}$/);
	if (match) {
		return match[1].trim();
	}
	return null;
}

export function isPdfFile(signedUrl: string | null, fallbackFileName?: string): boolean {
	if (signedUrl) {
		try {
			const url = new URL(signedUrl);
			return url.pathname.toLowerCase().endsWith(".pdf");
		} catch {
			const pathBeforeQuery = signedUrl.split("?")[0];
			return pathBeforeQuery.toLowerCase().endsWith(".pdf");
		}
	}
	if (fallbackFileName) {
		return fallbackFileName.toLowerCase().endsWith(".pdf");
	}
	return false;
}

export function convertDateToDayJs(dateString?: string): Dayjs | null {
	if (!dateString) return null;
	try {
		const date = dayjs(dateString, "MM/DD/YYYY");
		return date.isValid() ? date : null;
	} catch {
		return null;
	}
}

export function formatDateForAPI(date: Dayjs | null): string {
	if (!date) return "";
	return date.format("MM/DD/YYYY");
}

export const processDropdownOptions = (
	options: string[],
	applicants: Record<string, TEDCApplicant>
): ProcessedDropdownOptions => {
	const labelMap: Record<string, string> = {};

	const processed = options.map((option) => {
		const appIdMatch = option.match(/^app_id_(?:\{\{)?(\w+)(?:\}\})?$/);

		if (appIdMatch) {
			const appId = appIdMatch[1];
			const applicant = applicants[appId];

			if (applicant) {
				const travellerName = getFullName(
					applicant.traveller.first_name,
					applicant.traveller.middle_name,
					applicant.traveller.last_name
				);
				labelMap[option] = travellerName;
			}
		}

		return option;
	});

	return { processedOptions: processed, optionLabelMap: labelMap };
};

